import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { foundationApi } from '@/api/client';

// ============================================
// Types
// ============================================

export interface AuditLog {
  organization_id: string;
  resource_type: string;
  resource_id: string;
  resource_name: string;
  action: string;
  actor_id: string;
  actor_email: string;
  /**
   * Staff-impersonation attribution. When a DataCentral employee performed the
   * action on the customer's behalf, `actor_email` stays the customer account
   * acted upon and these carry the staff member who actually did it. Absent for
   * a direct customer action.
   */
  impersonator_email?: string;
  impersonator_id?: string;
  status: 'success' | 'failure';
  error_message?: string;
  /** JSON string — present on mutations (may be null). */
  old_value?: string | null;
  /** JSON string — present on mutations. */
  new_value?: string | null;
  client_ip?: string;
  user_agent?: string;
  /** "YYYY-MM-DD HH:mm:ss" */
  created_at: string;
}

export interface AuditPagination {
  page: number;
  per_page: number;
  total: number;
}

export interface AuditLogListResult {
  data: AuditLog[];
  pagination: AuditPagination;
}

export interface AuditLogParams {
  page: number;
  per_page?: number;
  resource_type?: string;
  resource_name?: string;
  resource_id?: string;
  action?: string;
  actor_id?: string;
  /** Empty string is treated as "all" — the hook omits it from the query string. */
  status?: 'success' | 'failure' | '';
  start_date?: string;
  end_date?: string;
}

const PER_PAGE = 20;

// ============================================
// Query keys
// ============================================

export const auditKeys = {
  all: ['audit'] as const,
  logs: (p: AuditLogParams) =>
    [
      'audit', 'logs',
      p.page, p.per_page ?? PER_PAGE,
      p.resource_type ?? '', p.resource_name ?? '', p.resource_id ?? '',
      p.action ?? '', p.actor_id ?? '', p.status ?? '',
      p.start_date ?? '', p.end_date ?? '',
    ] as const,
};

// ============================================
// Hooks
// ============================================

/**
 * Server-paginated audit log list. Mirrors `useDomainListPaged` conventions:
 * keyed by every query param, `keepPreviousData` for smooth pagination.
 */
export function useAuditLogs(params: AuditLogParams) {
  return useQuery({
    queryKey: auditKeys.logs(params),
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: async (): Promise<AuditLogListResult> => {
      const q = new URLSearchParams();
      q.set('page', String(params.page));
      q.set('per_page', String(params.per_page ?? PER_PAGE));
      if (params.resource_type) q.set('resource_type', params.resource_type);
      if (params.resource_name) q.set('resource_name', params.resource_name);
      if (params.resource_id) q.set('resource_id', params.resource_id);
      if (params.action) q.set('action', params.action);
      if (params.actor_id) q.set('actor_id', params.actor_id);
      if (params.status) q.set('status', params.status);
      if (params.start_date) q.set('start_date', params.start_date);
      if (params.end_date) q.set('end_date', params.end_date);

      const res = await foundationApi.get<{
        data: AuditLog[];
        pagination: AuditPagination;
      }>(`/audit/logs?${q.toString()}`);

      return {
        data: res.data?.data ?? [],
        pagination: res.data?.pagination ?? {
          page: params.page,
          per_page: params.per_page ?? PER_PAGE,
          total: 0,
        },
      };
    },
  });
}
